/*
 * <pickup-chooser> — how the customer picks up, asked in the cart BEFORE checkout.
 *
 * Replaces Zapiet (proposals/2026-09-23-storefront-pickup-without-zapiet.md). William, 2026-09-25:
 * Apple Pay and Shop Pay must work, and the store is not on Plus, so nothing can ask inside
 * checkout — the cart asks: in-store or route → which stop → which day (and the hour, in-store).
 * Until that is answered, Checkout and the express buttons are held back.
 *
 * What it writes is exactly what Zapiet wrote, so the SWRFM app reads a chooser order unchanged:
 *   cart attributes  Pickup-Location-Id · Pickup-Date (2026/10/02) · Pickup-Time ·
 *                    Pickup-Location-Company · Pickup-Location-Address-Line-1/-City/-Region/
 *                    -Postal-Code/-Country · Checkout-Method=pickup · Custom-Attribute-1
 *   one fee line     the "Pickup" product (In-Store $0.99 · Route $15.99 · Naples Route $24.99),
 *                    with Stop / Day / Time as line properties, so checkout shows the answer.
 *
 * The options come from the app (GET /api/storefront/pickup-options — the published pickup
 * policy, computed by the same date rule the app's order intake uses). The element is re-rendered
 * by the theme on every cart change (refreshCart replaces #AjaxCartSubtotal), so everything it
 * needs about the saved choice arrives as data attributes rendered by Liquid from the cart itself.
 */
(function () {
  if (customElements.get('pickup-chooser')) return;

  var OPTIONS_TTL_MS = 60 * 1000;
  var OPTIONS_TIMEOUT_MS = 8000;
  var DRAFT_KEY = 'swrfm-pickup-draft';

  function wait(ms) { return new Promise(function (done) { setTimeout(done, ms); }); }

  function getJson(url) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, OPTIONS_TIMEOUT_MS) : null;
    return fetch(url, { credentials: 'omit', signal: ctl ? ctl.signal : undefined }).then(function (r) {
      if (!r.ok) throw new Error('status ' + r.status);
      return r.json();
    }).then(function (body) {
      clearTimeout(timer);
      if (!body || !Array.isArray(body.routes)) throw new Error('not pickup options');
      return body;
    }, function (e) { clearTimeout(timer); throw e; });
  }

  // Two roads to the same answer, because a cart that cannot ask is a customer who cannot pay.
  // The app's own address is the fast one (cached at the edge). The store's address — Shopify's
  // app proxy — is the one no phone refuses: it is the site the customer is already on, so a
  // content blocker, a filtered network or a VPN that will not talk to the app's domain never
  // sees it. Then the first road once more, for a connection that dropped mid-request (a phone
  // switched to another app does exactly that). `why` tells the app's log what the first road did.
  function fetchOptions(endpoint, proxy) {
    var cache = window.__swrfmPickupOptions;
    if (cache && Date.now() - cache.at < OPTIONS_TTL_MS) return cache.promise;
    var promise = getJson(endpoint).catch(function (first) {
      var why = encodeURIComponent(String((first && (first.name + ' ' + first.message)) || first).slice(0, 80));
      function again() { return wait(1500).then(function () { return getJson(endpoint); }); }
      if (!proxy) return again();
      return getJson(proxy + (proxy.indexOf('?') === -1 ? '?' : '&') + 'via=store&why=' + why).catch(again);
    });
    window.__swrfmPickupOptions = { at: Date.now(), promise: promise };
    promise.catch(function () { window.__swrfmPickupOptions = null; });
    return promise;
  }

  function readDraft() {
    try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null') || {}; } catch (e) { return {}; }
  }
  function writeDraft(d) {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch (e) { /* private mode */ }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  // A US mobile number: 10 digits, or 11 starting with 1. Returns '(954) 854-4185' or ''.
  function usPhone(v) {
    var d = String(v || '').replace(/\D/g, '');
    if (d.length === 11 && d[0] === '1') d = d.slice(1);
    return d.length === 10 ? '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6) : '';
  }
  function rootUrl() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }

  // THE TEXT-MESSAGE CONSENT, word for word (2026-10-06). The checkbox writes `SMS-Consent` =
  // SMS_CONSENT_VERSION, and the app records the sentence that version stands for
  // (src/lib/sms-consent-text.ts, CART_CONSENT.v2) — so these words and those must be the same.
  // scripts/sms.test.mjs compares them. A wording change is a new version on both sides.
  var SMS_CONSENT_VERSION = 'v2';
  var SMS_CONSENT_LEAD = 'Text me about this order.';
  var SMS_CONSENT_REST = 'Southwest Ranches Farmers Market will text updates about this order (ready for pickup, ' +
    'missing items, reminders) to this number, up to 5 messages per order. Consent is not a condition of purchase. ' +
    'Message and data rates may apply. Reply STOP to opt out, HELP for help. See our {terms} and {privacy}.';
  function smsConsentHtml() {
    var link = function (path, text) { return '<a href="' + rootUrl() + 'policies/' + path + '" target="_blank" rel="noopener">' + text + '</a>'; };
    return '<b>' + esc(SMS_CONSENT_LEAD) + '</b> ' + esc(SMS_CONSENT_REST)
      .replace('{terms}', link('terms-of-service', 'Terms of Service'))
      .replace('{privacy}', link('privacy-policy', 'Privacy Policy'));
  }

  // Merge `extra` into the Pickup line's properties (cart/change.js replaces them wholesale).
  function syncFeeLine(extra) {
    return fetch(rootUrl() + 'cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var line = c.items.filter(function (i) { return i.handle === 'pickup-fee'; })[0];
      if (!line) return null;
      var props = {};
      Object.keys(line.properties || {}).forEach(function (k) { props[k] = line.properties[k]; });
      Object.keys(extra).forEach(function (k) { props[k] = extra[k]; });
      return fetch(rootUrl() + 'cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 1, properties: props }),
      });
    }).catch(function () { return null; });
  }

  // ROUTE300 (the market's standing offer): $300+ in goods on a WEEKLY route takes the $15.99 fee
  // off the Pickup line itself (scripts/route300-discount.mjs). Naples is not part of it
  // (Carolina, 2026-09-29), and in-store never was. The cart puts the code on the order, so nobody
  // has to type it: William pasted it at checkout on 2026-09-29 and read a fee that still said
  // $15.99 as a code that had failed.
  var FREE_ROUTE_CENTS = 30000;
  var FREE_ROUTE_CODE = 'ROUTE300';
  var FREE_ROUTE_TRIED_KEY = 'swrfm-route300-tried';
  function inFreeRouteOffer(route) { return !!route && route.kind === 'route' && route.schedule !== 'first-saturday'; }
  // `saved`: the pickup is on the cart. Before that the code is not there yet, and the cart is
  // about to add it; after that, a cart without it is one Shopify refused it on.
  // The offer as a bar that fills toward $300 (William, 2026-10-02: "a progress bar that shows
  // the current value and the value to reach"). One line under it says what is left, or that it
  // is done; the code itself is still applied by the chooser, never typed.
  var FREE_ROUTE_SCOPE = 'Counts the products of this pickup order only \u2014 memberships and the pickup fee don\u2019t count.';
  function freeRouteLine(goodsCents, applied, saved) {
    var pct = Math.max(0, Math.min(100, Math.round(goodsCents / FREE_ROUTE_CENTS * 100)));
    var done = applied || goodsCents >= FREE_ROUTE_CENTS;
    var line = applied
      ? '🎉 <strong>Your route fee is free</strong> — ' + FREE_ROUTE_CODE + ' is on your order.'
      : done
        ? (saved ? 'Orders of $300+ in products get a free route fee with code <strong>' + FREE_ROUTE_CODE + '</strong>.' : '🎉 <strong>Free route fee unlocked</strong> — we add it when you choose your day.')
        : '<strong>' + money(FREE_ROUTE_CENTS - goodsCents) + ' more</strong> for a free route fee';
    return '<div class="pc__free' + (done ? ' pc__free--done' : '') + '">' +
      // What counts is a tooltip on the goal (William, 2026-10-07: "make this text as tooltip"), opened
      // by hover, focus or a tap — never a sentence under the bar.
      '<div class="pc__bar-row"><span class="pc__bar-now">' + money(goodsCents) + '</span><span class="pc__bar-goal">Free route fee at ' + money(FREE_ROUTE_CENTS) + ' <button type="button" class="pc__tip" aria-label="' + FREE_ROUTE_SCOPE + '" tabindex="0" data-tip="' + FREE_ROUTE_SCOPE + '">i</button></span></div>' +
      '<div class="pc__bar" role="progressbar" aria-valuemin="0" aria-valuemax="' + FREE_ROUTE_CENTS + '" aria-valuenow="' + Math.min(goodsCents, FREE_ROUTE_CENTS) + '"><span class="pc__bar-fill" style="width:' + pct + '%"></span></div>' +
      '<div class="pc__bar-line">' + line + '</div></div>';
  }
  // Said only to a cart that would have earned it: the banner promises a free route fee, and a
  // Naples customer at $300 deserves to read why theirs is still charged.
  // Carolina, sync #15 (2026-10-02): said whenever a Naples stop is the choice, in her capitals,
  // not only to a cart that has reached $300.
  function noFreeRouteLine() {
    return '<div class="pc__free">Promo <strong>FREE ROUTE FEE</strong> does not apply for Naples location.</div>';
  }
  // Puts ROUTE300 on the cart, beside any code already there. Asked once per cart state: a code
  // Shopify refuses (another discount that will not combine) must not be asked for on every render.
  function applyFreeRoute(state) {
    try {
      if (sessionStorage.getItem(FREE_ROUTE_TRIED_KEY) === state) return Promise.resolve(false);
      sessionStorage.setItem(FREE_ROUTE_TRIED_KEY, state);
    } catch (e) { return Promise.resolve(false); }
    function isOurs(d) { return String(d.code || '').toUpperCase() === FREE_ROUTE_CODE; }
    return fetch(rootUrl() + 'cart.js').then(function (r) { return r.json(); }).then(function (c) {
      var codes = (c.discount_codes || []).filter(function (d) { return !isOurs(d); }).map(function (d) { return d.code; });
      return fetch(rootUrl() + 'cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.concat(FREE_ROUTE_CODE).join(',') }),
      });
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (c) {
      return !!c && (c.discount_codes || []).some(function (d) { return isOurs(d) && d.applicable; });
    }).catch(function () { return false; });
  }

  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  // THE MEMBERSHIP BOX DISCLAIMER (Carolina, sync #15; William, 2026-10-05): boxes are by schedule
  // only, and the first box depends on the plan in the cart AND a five-day rule — a membership
  // taken fewer than 5 days before a pickup day starts with the following one. Once a month picks
  // up on the 4th-Friday dates (the schedule marks them 'both'); twice a month on every date. The
  // dates are said here in full, never sent to a schedule link.
  var BOX_LEAD_DAYS = 5;
  // The member's next box days for the plan, as words ("Fri, Oct 23"), at most two.
  function nextBoxDays(plan, dates) {
    var today = new Date(); today.setHours(0, 0, 0, 0);
    return (dates || []).filter(function (d) {
      if (plan !== 'twice' && d.plans !== 'both') return false;
      var p = d.day.split('-'); var dt = new Date(+p[0], +p[1] - 1, +p[2]);
      return (dt.getTime() - today.getTime()) / 86400000 >= BOX_LEAD_DAYS;
    }).slice(0, 2).map(function (d) { var p = d.day.split('-'); var dt = new Date(+p[0], +p[1] - 1, +p[2]); return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); });
  }
  function boxLine(plan, dates) {
    var next = nextBoxDays(plan, dates);
    var planWord = plan === 'twice' ? 'twice a month' : 'once a month';
    return '<div class="pc__box" role="note"><span class="pc__box-title">\u26A0\uFE0F YOUR BOX IS BY SCHEDULE ONLY</span>' +
      (next.length ? 'Your first box (' + planWord + '): <strong>' + esc(next[0]) + '</strong>' + (next[1] ? ', then <strong>' + esc(next[1]) + '</strong>' : '') + '. ' : 'Your first box: the next scheduled pickup for your plan. ') +
      'A membership must be in place <strong>' + BOX_LEAD_DAYS + ' days before</strong> a pickup day to receive that box; later than that, it starts with the one after. ' +
      'Choose below where you pick up your box.</div>';
  }
  // The cart page's membership step (2026-10-14): the first box as one line, and the rules once, in
  // "How it works" — the same facts as boxLine, laid out like the beef and turkey steps.
  function firstBoxLine(plan, dates) {
    var next = nextBoxDays(plan, dates);
    var planWord = plan === 'twice' ? 'twice a month' : 'once a month';
    return '<p class="pc__first">' + (next.length ? 'Your first box (' + planWord + '): <strong>' + esc(next[0]) + '</strong>' + (next[1] ? ', then <strong>' + esc(next[1]) + '</strong>' : '') + '.' : 'Your first box: the next scheduled pickup for your plan.') + '</p>';
  }
  function boxHow() {
    return '<div class="swr-how"><h3 class="swr-how__t">How it works</h3><ul>' +
      '<li><strong>Box days.</strong> Once a month: one scheduled box day each month. Twice a month: every scheduled box day.</li>' +
      '<li><strong>' + BOX_LEAD_DAYS + ' days ahead.</strong> A membership must be in place ' + BOX_LEAD_DAYS + ' days before a pickup day to receive that box; later than that, it starts with the one after.</li>' +
      '</ul></div>';
  }
  // The stop's address as a "Directions" link into Google Maps (William, 2026-10-03: "a direction
  // link to Google Maps instead of just a plain address"). Coordinates when the stop has them,
  // the address otherwise.
  // Under it, the place ON A MAP (William, 2026-10-06: "must show the location in the map to be
  // recognisable for the users") — Google's keyless embed centred on the stop, so no API key
  // reaches the browser; lazy, so the cart does not pay for it until the place is chosen.
  // ADD TO CALENDAR, in the cart's Payment review (William, 2026-10-07). The saved pickup as one
  // event: "SWRFM pickup — <route | In-store>", at the stop's hour (or the chosen in-store time) for
  // thirty minutes in the market's zone — the rule src/lib/pickup-calendar.ts uses for the emails —
  // at the stop's address, with the order note and Google Maps directions. Google gets a template
  // link; Apple Calendar and Outlook get an .ics built here, in the browser.
  var MARKET_TZ = 'America/New_York';
  function clockMinutes(t) {
    var m = String(t || '').match(/(0?[1-9]|1[0-2]):([0-5]\d)\s*([AP])\.?M/i);
    return m ? ((+m[1] % 12) + (m[3].toUpperCase() === 'P' ? 12 : 0)) * 60 + (+m[2]) : null;
  }
  function marketToUtc(y, mo, d, hh, mm) {
    var asUtc = Date.UTC(y, mo - 1, d, hh, mm);
    var parts = new Intl.DateTimeFormat('en-US', { timeZone: MARKET_TZ, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(asUtc));
    var get = function (k) { var p = parts.filter(function (x) { return x.type === k; })[0]; return p ? +p.value : 0; };
    var et = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
    return new Date(asUtc - (et - asUtc));
  }
  function stampUtc(d) { return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z'); }
  function a2(a) { return [a.line1, a.city, [a.region, a.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', '); }
  function pickupCalendar(f) {
    var p = String(f.day || '').split('-');
    if (p.length !== 3) return null;
    var min = clockMinutes(f.time), dates, icsWhen;
    if (min == null) {
      var next = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2] + 1)).toISOString().slice(0, 10).replace(/-/g, '');
      dates = p.join('') + '/' + next;
      icsWhen = ['DTSTART;VALUE=DATE:' + p.join(''), 'DTEND;VALUE=DATE:' + next];
    } else {
      var start = marketToUtc(+p[0], +p[1], +p[2], Math.floor(min / 60), min % 60), end = new Date(start.getTime() + 30 * 60000);
      dates = stampUtc(start) + '/' + stampUtc(end);
      icsWhen = ['DTSTART:' + stampUtc(start), 'DTEND:' + stampUtc(end)];
    }
    var q = 'action=TEMPLATE&text=' + encodeURIComponent(f.title) + '&dates=' + dates + '&details=' + encodeURIComponent(f.description) + '&location=' + encodeURIComponent(f.location || '') + '&ctz=' + MARKET_TZ;
    var e = function (t) { return String(t).replace(/\\/g, '\\\\').replace(/;/g, '\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n'); };
    var ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//SWRFM//Cart pickup//EN', 'METHOD:PUBLISH', 'BEGIN:VEVENT', 'UID:cart-' + f.day + '-' + Date.now() + '@swrfmarket.com', 'DTSTAMP:' + stampUtc(new Date())]
      .concat(icsWhen, ['SUMMARY:' + e(f.title), 'LOCATION:' + e(f.location || ''), 'DESCRIPTION:' + e(f.description), 'END:VEVENT', 'END:VCALENDAR']).join('\r\n') + '\r\n';
    return { google: 'https://calendar.google.com/calendar/render?' + q, ics: ics };
  }
  // The control, drawn into [data-swr-cal] (snippets/cart-subtotal.liquid) — also re-drawn by the
  // cart's tabs controller after the totals column is redrawn, from window.__swrCalHtml.
  function fillCalendar() { document.querySelectorAll('[data-swr-cal]').forEach(function (el) { if (el.innerHTML !== (window.__swrCalHtml || '')) el.innerHTML = window.__swrCalHtml || ''; }); }
  document.addEventListener('click', function (ev) {
    var b = ev.target.closest && ev.target.closest('[data-swr-ics]');
    if (!b || !window.__swrCalIcs) return;
    ev.preventDefault();
    var url = URL.createObjectURL(new Blob([window.__swrCalIcs], { type: 'text/calendar;charset=utf-8' }));
    var a = document.createElement('a'); a.href = url; a.download = 'swrfm-pickup.ics'; document.body.appendChild(a); a.click();
    setTimeout(function () { URL.revokeObjectURL(url); a.remove(); }, 1000);
  });
  window.__swrFillCalendar = fillCalendar;

  function directionsLine(stop) {
    var a = stop.address || {};
    var text = [a.line1, a.city].filter(Boolean).join(', ');
    if (!text) return '';
    var dest = stop.lat && stop.lng ? stop.lat + ',' + stop.lng : text;
    var map = '<iframe class="pc__map" src="https://maps.google.com/maps?q=' + encodeURIComponent(dest) + '&z=15&output=embed" loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Map of ' + esc(text) + '"></iframe>';
    // The stop's own details under the map (William, 2026-10-06: "a disclaimer about the detailed
    // info of each location chosen is important to show there too") — the meeting point and how
    // long the van stays, as Routes & stops writes them. Back here after being dropped on 2026-10-02.
    var meet = stop.notes ? '<div class="pc__meet"><strong>Where exactly, and when</strong>' + esc(stop.notes) + '</div>' : '';
    return '<div class="pc__note"><a class="pc__directions" href="https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest) + '" target="_blank" rel="noopener">\uD83D\uDCCD ' + esc(text) + ' \u00b7 <u>Directions</u></a>' + map + meet + '</div>';
  }

  // Saving re-renders the cart, so the element that answers is a new one: this tells it that the
  // customer just chose, and that what it shows next is what they are waiting to see.
  var REVEAL_MS = 5000;
  function askReveal() { window.__swrfmPickupReveal = Date.now(); }
  function revealAsked() { return Date.now() - (window.__swrfmPickupReveal || 0) < REVEAL_MS; }
  function slashDate(iso) { return iso.replace(/-/g, '/'); }
  function isoDate(v) { return (v || '').trim().replace(/\//g, '-'); }

  // EVERY pickup-fee line, zeroed by its line KEY (2026-10-07, live: a cart carried three fee lines,
  // ~$45). `updates` keyed by VARIANT id touches one line only, and fee lines with different
  // properties (stop, day, time) are separate lines, so each re-save left the last one behind.
  // Returns the update to send with the attributes; one fee line is added after it, never more.
  function feeLineZeroes() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
      var updates = {};
      (c.items || []).forEach(function (i) { if (i.handle === 'pickup-fee') updates[i.key] = 0; });
      return updates;
    });
  }

  class PickupChooser extends HTMLElement {
    connectedCallback() {
      this.readData();
      // The checkout button lives in the totals column while the chooser sits in the steps: the page is the scope.
      this.scope = document;
      this.gate(false);

      // An empty cart carries no pickup: drop a leftover fee line and the attributes.
      if (this.goods === 0) {
        if (this.feeLines.length || this.saved.locationId) this.clear();
        this.innerHTML = '';
        return;
      }

      var draft = readDraft();
      this.state = { kind: draft.kind || '', stopId: draft.stopId || '', date: draft.date || '', time: draft.time || '' };
      this.load();
    }

    // Everything the cart says about itself, from the data attributes Liquid rendered.
    readData() {
      this.fee = JSON.parse(this.dataset.feeVariants || '{}'); // { 'In-Store': {id, price}, ... }
      this.saved = {
        locationId: this.dataset.locationId || '',
        date: isoDate(this.dataset.date),
        time: (this.dataset.time || '').trim(),
        mobile: (this.dataset.mobile || '').trim(),
        // `v2` is the sentence below; an older `yes` (the bare label) shows unticked, so the
        // customer agrees again to the words carriers require.
        sms: (this.dataset.sms || '').trim() === SMS_CONSENT_VERSION,
      };
      // The number a signed-in customer already gave us (profile, else their last order).
      this.knownPhone = usPhone(this.dataset.knownPhone);
      // The signed-in customer's usual pickup (set on account.swrfmarket.com → Pickup orders).
      try { this.pref = JSON.parse(this.dataset.pref || 'null'); } catch (e) { this.pref = null; }
      this.goods = parseInt(this.dataset.goods || '0', 10);
      // Products of the pickup order (not the box, not the fee); 0 = a membership-only cart.
      this.pickupGoods = parseInt(this.dataset.pickupGoods || String(this.goods), 10);
      // Where the member picks up the BOX — asked in the membership step, saved as a cart attribute
      // the order carries to the app (subscription_delivery_prefs, by the orders webhook).
      this.boxStop = (this.dataset.boxStop || '').trim();
      if (this.mobileOk === undefined) this.mobileOk = false;
      this.boxPlan = (this.dataset.boxPlan || '').trim();
      // A Thanksgiving turkey deposit (2026-10-12): its day is asked in its own step
      // (snippets/swrfm-turkey-step.liquid) and saved as the `Turkey-Pickup` cart attribute.
      this.turkey = parseInt(this.dataset.turkey || '0', 10);
      this.turkeyPickup = (this.dataset.turkeyPickup || '').trim();
      // A beef package (2026-10-14): its approximate day, 4 to 6 weeks out, is asked in its own step
      // (snippets/swrfm-beef-step.liquid) and saved as the `Beef-Pickup` cart attribute.
      this.beef = parseInt(this.dataset.beef || '0', 10);
      this.beefPickup = (this.dataset.beefPickup || '').trim();
      this.goodsCents = parseInt(this.dataset.goodsCents || '0', 10);
      this.freeRouteApplied = this.dataset.freeRouteApplied === 'true';
      this.feeLines = JSON.parse(this.dataset.feeLines || '[]'); // [{variant, qty}]
    }

    // SAVE IN PLACE (William, 2026-10-07: pressing Save made the Pickup details tab close and reopen).
    // On the cart page the theme's refreshCart redraws the whole form, this chooser included, which
    // then loads again from nothing. Here only what the save changed is redrawn — the products and
    // membership screens (the Pickup fee line), the totals, the cart count — and this element keeps
    // its DOM: it takes the new data attributes, re-reads them and renders itself, at the same scroll.
    // Anywhere else (or if the screens changed shape) it falls back to the theme's refresh.
    softRefresh() {
      var form = document.getElementById('AjaxCartForm');
      var full = function () { if (typeof window.refreshCart === 'function') window.refreshCart(false); else location.reload(); };
      if (!form || !form.contains(this) || !document.querySelector('[data-swr-screen]')) { full(); return Promise.resolve(); }
      var self = this, y = window.scrollY;
      return fetch('?section_id=helper-cart').then(function (r) { return r.text(); }).then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var nf = doc.getElementById('AjaxCartForm'), ns = doc.getElementById('AjaxCartSubtotal'), nc = nf && nf.querySelector('pickup-chooser');
        if (!nf || !ns || !nc) throw new Error('shape');
        ['products', 'membership'].forEach(function (k) {
          var old = form.querySelector('[data-swr-screen="' + k + '"]'), nw = nf.querySelector('[data-swr-screen="' + k + '"]');
          if (!old !== !nw) throw new Error('shape');
          if (old) { nw.hidden = old.hidden; old.replaceWith(document.importNode(nw, true)); }
        });
        ['[data-cart-count]', '[data-cart-total]'].forEach(function (sel) { var o = form.querySelector(sel), n = nf.querySelector(sel); if (o && n) o.textContent = n.textContent; });
        if (typeof form.ajaxifyCartItems === 'function') form.ajaxifyCartItems();
        document.querySelectorAll('[data-header-cart-count]').forEach(function (e) { e.textContent = form.querySelector('[data-cart-count]').textContent; });
        document.querySelectorAll('[data-header-cart-total]').forEach(function (e) { e.textContent = form.querySelector('[data-cart-total]').textContent; });
        var sub = document.getElementById('AjaxCartSubtotal');
        if (sub) sub.innerHTML = ns.innerHTML;
        Array.prototype.forEach.call(nc.attributes, function (at) { if (at.name.indexOf('data-') === 0) self.setAttribute(at.name, at.value); });
        self.readData();
        self.editing = false;
        self.adoptSaved();
        self.render();
        window.scrollTo(0, y);
      }).catch(full);
    }

    disconnectedCallback() { this.unwatch(); }

    load() {
      this.unwatch();
      this.innerHTML = '<div class="pc pc--loading">Loading pickup options…</div>';
      var self = this;
      if (this.boxPlan && !this.boxDates) {
        // The box schedule, from the app (the same host as the pickup options).
        fetch(String(this.dataset.endpoint).replace(/pickup-options$/, 'box-schedule'), { credentials: 'omit' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) { if (j && j.pickups) { self.boxDates = j.pickups; if (self.options) self.render(); } })
          .catch(function () {});
      }
      fetchOptions(this.dataset.endpoint, this.dataset.proxy).then(function (opts) {
        self.options = opts;
        try {
          if (self.heal()) return;
          self.adoptSaved();
          self.render();
        } catch (e) {
          // What the cart remembered (a draft, a saved choice, the usual pickup) must never cost
          // the customer the question itself: forget it and ask from the start.
          try { sessionStorage.removeItem(DRAFT_KEY); } catch (e2) { /* ignore */ }
          self.saved.locationId = '';
          self.pref = null;
          self.valid = false;
          self.editing = false;
          self.state = { kind: '', stopId: '', date: '', time: '' };
          self.render();
        }
      }).catch(function () { self.failed(); });
    }

    // Never a dead end: a button to ask again, and the phone asks by itself when it comes back —
    // from another app, or from a lost signal.
    failed() {
      this.innerHTML = '<div class="pc pc--error">We could not load pickup options. ' +
        '<button type="button" class="pc__retry">Try again</button>, or call (954) 854-4185.</div>';
      var self = this;
      function back() { if (document.visibilityState !== 'hidden' && self.isConnected) self.load(); }
      this.querySelector('.pc__retry').addEventListener('click', back);
      document.addEventListener('visibilitychange', back);
      window.addEventListener('online', back);
      this.unwatch = function () {
        document.removeEventListener('visibilitychange', back);
        window.removeEventListener('online', back);
        self.unwatch = function () {};
      };
    }

    unwatch() {}

    // Everything the options know about a stop, by its location id.
    findStop(locationId) {
      var routes = (this.options && this.options.routes) || [];
      for (var i = 0; i < routes.length; i++) {
        for (var j = 0; j < routes[i].stops.length; j++) {
          if (routes[i].stops[j].locationId === locationId) return { route: routes[i], stop: routes[i].stops[j] };
        }
      }
      return null;
    }
    findStopById(stopId) {
      var routes = (this.options && this.options.routes) || [];
      for (var i = 0; i < routes.length; i++) {
        for (var j = 0; j < routes[i].stops.length; j++) {
          if (routes[i].stops[j].id === stopId) return { route: routes[i], stop: routes[i].stops[j] };
        }
      }
      return null;
    }
    feeFor(route) {
      if (route.kind === 'in-store') return this.fee['In-Store'];
      if (route.schedule === 'first-saturday') return this.fee['Naples Route'];
      return this.fee['Route'];
    }

    // ONE PICKUP FEE LINE PER CART, AT QUANTITY 1 (2026-10-07). A cart with several, or one at 2+, is
    // put right on load: re-saved as the pickup the cart says, or cleared when that pickup is gone. A
    // membership-only cart carries none — the box has no fee line of its own. Returns true when it acted.
    heal() {
      var lines = this.feeLines || [];
      var extra = lines.length > 1 || lines.some(function (l) { return l.qty > 1; });
      if (this.pickupGoods === 0 && lines.length) { this.clear(); return true; }
      if (!extra || this._healed) return false;
      this._healed = true;
      var hit = this.saved.locationId && this.findStop(this.saved.locationId);
      var day = hit && hit.stop.dates.filter(function (d) { return d.date === this.saved.date; }, this)[0];
      if (hit && day) {
        this.state = { kind: hit.route.kind, stopId: hit.route.kind === 'in-store' ? '' : hit.stop.id, date: this.saved.date, time: this.saved.time };
        this.save();
      } else {
        this.clear();
      }
      return true;
    }

    // The saved choice is valid when the cart still says it AND the rule still offers it.
    adoptSaved() {
      this.valid = false;
      var hit = this.saved.locationId && this.findStop(this.saved.locationId);
      if (!hit) { this.adoptPreference(); return; }
      var day = hit.stop.dates.filter(function (d) { return d.date === this.saved.date; }, this)[0];
      var fee = this.feeFor(hit.route);
      var feeOk = fee && this.feeLines.length === 1 && String(this.feeLines[0].variant) === String(fee.id) && this.feeLines[0].qty === 1;
      var timeOk = hit.route.kind !== 'in-store' || (day && day.times && day.times.indexOf(this.saved.time) !== -1);
      if (day && feeOk && timeOk) {
        this.valid = true;
        this.state = { kind: hit.route.kind, stopId: hit.stop.id, date: this.saved.date, time: this.saved.time };
      } else if (!this.state.kind) {
        // Keep what they had picked so they only fix what went stale (e.g. last week's date).
        this.state = { kind: hit.route.kind, stopId: hit.stop.id, date: day ? this.saved.date : '', time: '' };
      }
    }

    // Nothing saved on this cart yet: start from the customer's usual pickup, if it is still offered.
    // Only the place is pre-selected — the day is always theirs to choose.
    adoptPreference() {
      if (this.state.kind || !this.pref || !this.pref.stopId) return;
      var hit = this.findStopById(this.pref.stopId);
      if (!hit) return;
      this.state = { kind: hit.route.kind, stopId: hit.route.kind === 'in-store' ? '' : hit.stop.id, date: '', time: '' };
    }

    gate(open) {
      var scope = this.scope;
      if (!scope) return;
      // What each step needs, for the cart's step screens (snippets/cart-form-page.liquid): the pickup
      // order is ready with a saved pickup (or when there is none to ask) and a mobile number; the
      // membership with its box pickup spot. Payment opens when every step present is ready.
      var pickupReady = this.pickupGoods === 0 || (!!this.valid && !this.editing);
      var boxReady = !this.boxPlan || !!this.boxStop;
      var turkeyReady = !this.turkey || !!this.turkeyPickup;
      var beefReady = !this.beef || !!this.beefPickup;
      var all = !!open && pickupReady && boxReady && turkeyReady && beefReady;
      document.documentElement.classList.toggle('pickup-gate--closed', !all);
      scope.querySelectorAll('#CheckOut, button[name="checkout"]').forEach(function (b) { b.disabled = !all; });
      window.__swrSteps = { order: pickupReady && this.mobileOk, membership: boxReady && (this.pickupGoods > 0 || this.mobileOk), box: !!this.boxPlan, pickup: this.pickupGoods > 0, turkey: turkeyReady, hasTurkey: this.turkey > 0, beef: beefReady, hasBeef: this.beef > 0 };
      document.dispatchEvent(new CustomEvent('swr:steps', { detail: window.__swrSteps }));
    }

    render() {
      var o = this.options;
      var s = this.state;
      var inStore = o.routes.filter(function (r) { return r.kind === 'in-store'; })[0];
      var routes = o.routes.filter(function (r) { return r.kind === 'route'; });
      var routeFee = this.fee['Route'], naplesFee = this.fee['Naples Route'], storeFee = this.fee['In-Store'];

      // THE FORM NEVER FOLDS (William, 2026-10-06: "I don't like that we need to press Change to
      // review again all … all in line, and of course saved, but always showing the options to
      // change"). A saved pickup is shown selected, with a ✓ line that says so; any change saves by
      // itself and the theme redraws. The mobile number is the last card, asked once.
      var savedNow = this.valid && !this.editing;
      var savedHit = savedNow ? this.findStop(this.saved.locationId) : null;
      var savedDay = savedHit ? savedHit.stop.dates.filter(function (d) { return d.date === this.saved.date; }, this)[0] : null;

      this.gate(false);
      // The bar toward the free route fee leads the card (William, 2026-10-02), for any choice but
      // in-store, where the fee is $0.99 and the offer does not apply; a Naples stop reads why not.
      var askedStop = s.kind === 'route' && s.stopId ? this.findStopById(s.stopId) : null;
      var html = '<div class="pc">';
      var step = 0;
      var card = function (title) { step++; return '<div class="pc__card"><div class="pc__card-title"><span class="pc__step">' + step + '</span><span>' + title + '</span></div>'; };
      // The $300 offer first, in a frame of its own and with no step number — it is an incentive,
      // not a question (William, 2026-10-06: "this has to have their own space … it could not be in
      // the same part of this question"). Not for an in-store pickup, where the offer does not apply.
      // On the cart page it sits in the space beside the title (William, 2026-10-07: "use the space at
      // the top"), [data-swr-offer-slot]; with no pickup order (a membership only) there is no offer.
      var offerHtml = this.pickupGoods > 0 && s.kind !== 'in-store' ? '<div class="pc__card pc__card--offer">' + (!askedStop || inFreeRouteOffer(askedStop.route) ? freeRouteLine(this.goodsCents, this.freeRouteApplied && !!askedStop, false) : noFreeRouteLine()) + '</div>' : '';
      var offerSlot = document.querySelector('[data-swr-offer-slot]');
      if (offerSlot) offerSlot.innerHTML = offerHtml ? '<div class="pc">' + offerHtml + '</div>' : '';
      else html += offerHtml;
      // THE MEMBERSHIP IN ITS OWN GROUP (William, 2026-10-07: "split the cart when it holds a
      // membership"). On the cart page the line list has a slot under the box line
      // (snippets/cart-form-page.liquid, [data-swr-membership-slot]); the note and the once/twice
      // switch are drawn THERE, beside the box they describe, not among the pickup questions. In the
      // drawer there is no slot and they stay here, as before. One checkout either way.
      var slot = this.boxPlan ? document.querySelector('[data-swr-membership-slot]') : null;
      if (this.boxPlan && slot) {
        // The Payment tab's review says the first box too (snippets/cart-subtotal.liquid, [data-swr-first-box]).
        var first = this.boxDates ? (nextBoxDays(this.boxPlan, this.boxDates)[0] || 'The next scheduled pickup') : '';
        window.__swrFirstBox = first;
        document.querySelectorAll('[data-swr-first-box]').forEach(function (e) { e.textContent = first; });
        // The cart page's membership step has the special steps' one look (2026-10-14): the choices first —
        // plan, first box, where the box is picked up — then one "How it works" list, each rule said once.
        // The drawer (no slot) keeps boxLine below. Same data, same handlers; only the layout and words.
        slot.innerHTML = '<div class="pc"><div class="pc__card">' + this.planSwitch() + firstBoxLine(this.boxPlan, this.boxDates) + '</div>' + this.boxSpotCard(inStore, routes) + '</div>' + boxHow();
        var chooser = this;
        slot.querySelectorAll('[data-act="plan"]').forEach(function (el) {
          el.addEventListener('click', function () { chooser.switchPlan(Number(el.dataset.variant), el); });
        });
        var spot = slot.querySelector('#pc-box-stop');
        if (spot) spot.addEventListener('change', function () { chooser.saveBoxStop(spot.value, spot.options[spot.selectedIndex].text); });
      } else if (this.boxPlan) {
        html += card('Your membership') + this.planSwitch() + boxLine(this.boxPlan, this.boxDates) + '</div>';
      }
      if (this.pickupGoods > 0) {
      html += card('How do you want to pick up your order?');
      html += '<div class="pc__kinds">';
      if (inStore) {
        // The days come from the app's rule when it says them (`inStore.days`), so the card can
        // never promise a day the calendar under it does not offer.
        // The two cards carry a name and a price, nothing else (William, 2026-10-03); the days,
        // the cutoff and the offer are the bullets under them.
        html += this.kindCard('in-store', 'In-store pickup', storeFee ? money(storeFee.price) : '', '');
      }
      if (routes.length) {
        html += this.kindCard('route', 'Route pickup', routeFee ? money(routeFee.price) : '', '');
      }
      html += '</div>';
      // The rules every customer needs, as highlighted bullets under the two cards, whatever is
      // chosen (William, 2026-10-02: "highlighted and shown as bullet points … not just inside the
      // square of the selection"). The deadline and the in-store days come from the app's rules.
      var storeStop = inStore && inStore.stops && inStore.stops[0];
      var storeWhere = storeStop && storeStop.address ? [storeStop.address.line1, storeStop.address.city].filter(Boolean).join(', ') : '';
      // One place for the rules: the Details list. A one-line summary sat above it and said the same two
      // things again (William, 2026-10-13: "making a duplicate feeling since the expandable details are
      // more clear").
      html += '<details class="pc__more"><summary>Details</summary><ul class="pc__rules">' +
        '<li>' + esc(o.deadline.text) + '. Orders after that go onto next week\u2019s schedule.</li>' +
        // Where in-store IS, said before anything is chosen (a customer, 2026-10-06: "where do I go
        // physically to pick up my order? When I selected pick-up, I didn't see the location").
        (o.inStore && o.inStore.days ? '<li>In-store pickup: available ' + esc(o.inStore.days) + (storeWhere ? ' at the market, ' + esc(storeWhere) : '') + '.</li>' : '') +
        // The $300 offer is the bar above; the bullet only says where it does not apply (William, 2026-10-03).
        '<li>Promo <strong>FREE ROUTE FEE</strong> does not apply for Naples location.</li>' +
      '</ul></details>';
      html += '</div>';

      html += card('Where and when');
      if (savedNow && savedHit && savedDay) {
        var savedWhere = savedHit.route.kind === 'in-store' ? 'In-store at the market' : esc(savedHit.stop.label) + ' \u00b7 ' + esc(savedHit.route.label);
        var savedWhen = savedDay.label + (savedHit.route.kind === 'in-store' ? ' \u00b7 ' + esc(this.saved.time) : (savedHit.stop.time ? ' \u00b7 ' + esc(savedHit.stop.time) : ''));
        html += '<div class="pc__saved">\u2713 Saved: ' + savedWhere + ' \u00b7 ' + esc(savedWhen) + '<span class="pc__saved-hint">Change anything below and it saves by itself.</span></div>';
      }
      if (s.kind === 'route') {
        html += '<label class="pc__label" for="pc-stop">Which stop?</label><select id="pc-stop" class="pc__select" data-act="stop"><option value="">Choose a stop…</option>';
        // Fridays before Saturdays, the monthly route last; each stop says its own day.
        var dayRank = { Friday: 0, Saturday: 1 };
        routes.slice().sort(function (x, y) {
          var rx = x.schedule === 'first-saturday' ? 9 : (dayRank[x.weekday] ?? 5), ry = y.schedule === 'first-saturday' ? 9 : (dayRank[y.weekday] ?? 5);
          return rx - ry;
        }).forEach(function (r) {
          var day = r.schedule === 'first-saturday' ? '1st Sat' : (r.weekday ? r.weekday.slice(0, 3) : '');
          html += '<optgroup label="' + esc(r.label + (r.schedule === 'first-saturday' ? ' · first Saturday of the month' : r.weekday ? ' · ' + r.weekday + 's' : '')) + '">';
          r.stops.forEach(function (st) {
            html += '<option value="' + esc(st.id) + '"' + (st.id === s.stopId ? ' selected' : '') + '>' + esc(st.label + (day || st.time ? ' · ' + [day, st.time].filter(Boolean).join(' ') : '')) + '</option>';
          });
          html += '</optgroup>';
        });
        html += '</select>';
      }

      var chosen = s.kind === 'in-store' && inStore ? { route: inStore, stop: inStore.stops[0] } : (s.stopId ? this.findStopById(s.stopId) : null);
      if (chosen) {
        // The address as a Directions link — on a route AND in store (the market's address was never
        // shown here; the meeting instructions stay on the route page and in the pickup email).
        html += directionsLine(chosen.stop);
        if (!chosen.stop.dates.length) {
          html += '<div class="pc__hint">No pickup days are open right now. Please call (954) 854-4185.</div>';
        } else {
          html += '<div class="pc__label">Which day?</div>' + this.calendar(chosen.stop.dates, s.date);
          var day = chosen.stop.dates.filter(function (d) { return d.date === s.date; })[0];
          if (chosen.route.kind === 'in-store' && day) {
            html += '<div class="pc__label">What time?</div><div class="pc__chips">';
            day.times.forEach(function (t) {
              html += '<button type="button" class="pc__chip' + (t === s.time ? ' is-on' : '') + '" data-act="time" data-value="' + esc(t) + '" aria-pressed="' + (t === s.time) + '">' + esc(t) + '</button>';
            });
            html += '</div>';
          }
        }
      }
      if (!chosen) html += '<div class="pc__hint">' + (s.kind === 'route' ? 'Choose a stop above to see its map, meeting point and days.' : 'Choose in-store or route pickup above.') + '</div>';
      html += '</div>';
      // Two ways out of the form besides changing a dropdown: confirm what is filled in (a choice
      // restored from before, or the same stop picked again), or go back to the saved pickup.
      // ANY unsaved change shows Save (William, 2026-10-07: switching to in store showed nothing, so
      // the change looked lost). It is enabled once the choice is complete — a complete choice also
      // saves by itself — and until then it says what is still missing.
      if (!savedNow && (this.editing || this.complete())) {
        var need = this.complete() ? '' : (s.kind === 'route' ? (!s.stopId ? 'Choose a stop' : 'Choose a day') : (!s.kind ? 'Choose in-store or route' : (!s.date ? 'Choose a day' : 'Choose a time')));
        html += '<div class="pc__save-row"><button type="button" class="pc__save button button--solid button--small" data-act="save"' + (need ? ' disabled' : '') + '>Save pickup</button>' + (need ? '<span class="pc__save-need">' + need + ' to save.</span>' : '') + '</div>';
      }
      if (this.editing && this.valid) html += '<button type="button" class="pc__keep" data-act="keep">Back to my saved pickup</button>';
      }
      // The mobile number, asked here once (a pickup order has no shipping address to carry a phone;
      // express checkout skips every form, so the cart is the one place every order passes through).
      html += card('Your mobile number') +
        '<input id="pc-mobile" class="pc__select pc__mobile" type="tel" inputmode="tel" autocomplete="tel-national" placeholder="(954) 555-0123" value="' + esc(this.saved.mobile) + '">' +
        '<div class="pc__hint pc__mobile-hint"></div>' +
        '<label class="pc__sms"><input type="checkbox" id="pc-sms"' + (this.saved.sms ? ' checked' : '') + '> <span class="pc__sms-text">' + smsConsentHtml() + '</span></label>' +
      '</div>';
      if (!savedNow && this.pickupGoods > 0) html += '<div class="pc__gate-msg">' + (this.complete() ? 'Save your pickup to check out.' : 'Select your pick-up location to check out.') + '</div>';
      html += '</div>';
      this.innerHTML = html;
      this.bind();
      this.bindMobile();
      // No Add to calendar in the cart (2026-10-08): its redraw looped with the tabs controller's
      // observer and froze the page at Payment. The ready email carries the calendar.
      if (savedNow) {
        if (revealAsked() && this.getClientRects().length) { window.__swrfmPickupReveal = 0; this.reveal(true); }
        if (savedHit && inFreeRouteOffer(savedHit.route) && this.goodsCents >= FREE_ROUTE_CENTS && !this.freeRouteApplied) {
          applyFreeRoute(this.saved.locationId + ':' + this.goodsCents).then(function (applied) {
            if (applied) this.softRefresh();
          }.bind(this));
        }
      }
    }

    // The offered days as a Monday-first calendar: every week from the first offered day to the
    // last, only offered days tappable. Naples runs monthly, so its two dates are weeks apart — a
    // calendar that long is noise, and two buttons say it better.
    calendar(dates, selected) {
      function parse(iso) { var p = iso.split('-').map(Number); return new Date(Date.UTC(p[0], p[1] - 1, p[2])); }
      function iso(d) { return d.toISOString().slice(0, 10); }
      var offered = {};
      dates.forEach(function (d) { offered[d.date] = d; });
      var first = parse(dates[0].date), last = parse(dates[dates.length - 1].date);
      var start = new Date(first); start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
      var end = new Date(last); end.setUTCDate(end.getUTCDate() + (6 - ((end.getUTCDay() + 6) % 7)));
      var weeks = Math.round((end - start) / 864e5 + 1) / 7;
      if (weeks > 3) {
        var chips = '<div class="pc__chips">';
        dates.forEach(function (d) {
          var on = d.date === selected;
          chips += '<button type="button" class="pc__chip' + (on ? ' is-on' : '') + '" data-act="date" data-value="' + esc(d.date) + '" aria-pressed="' + on + '">' + esc(d.label) + '</button>';
        });
        return chips + '</div>';
      }
      var FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      var m1 = start.getUTCMonth(), m2 = end.getUTCMonth();
      var caption = m1 === m2 ? FULL[m1] : FULL[m1] + ' – ' + FULL[m2];
      var html = '<div class="pc__cal" role="group" aria-label="Pickup day"><div class="pc__cal-month">' + caption + '</div><div class="pc__cal-head">';
      ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach(function (w) { html += '<span>' + w + '</span>'; });
      html += '</div><div class="pc__cal-grid">';
      for (var d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        var key = iso(d);
        var num = d.getUTCDate();
        var label = String(num);
        if (offered[key]) {
          var on = key === selected;
          html += '<button type="button" class="pc__day' + (on ? ' is-on' : '') + '" data-act="date" data-value="' + key + '" aria-pressed="' + on + '" aria-label="' + esc(offered[key].label) + '">' + label + '</button>';
        } else {
          html += '<span class="pc__day is-off" aria-hidden="true">' + label + '</span>';
        }
      }
      return html + '</div></div>';
    }

    // In the cart drawer the chooser sits under the lines and the suggestions, so on a phone the
    // step a choice opens (the stops, the days, the hours) lands below the fold and the cart
    // looks as if it ignored the tap. After a choice, bring the question into view — the drawer
    // scrolls, never the page behind it. Skipped for the copy of the chooser that is not on
    // screen (the cart page carries the drawer's too).
    //
    // `settle`: after a save the theme redraws the whole cart and reloads its suggestions, which
    // land a moment later and push the chooser back down — so look again while that settles, and
    // stop the moment the customer scrolls for themselves.
    reveal(settle) {
      var self = this;
      function show() {
        if (!self.isConnected || !self.getClientRects().length) return;
        var box = self.getBoundingClientRect();
        var height = window.innerHeight || document.documentElement.clientHeight;
        if (box.top >= 0 && box.bottom <= height) return;
        try { self.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) { self.scrollIntoView(false); }
      }
      requestAnimationFrame(show);
      if (!settle) return;
      var timers = [400, 1200, 2500].map(function (ms) { return setTimeout(show, ms); });
      function stop() {
        timers.forEach(clearTimeout);
        ['touchstart', 'wheel', 'keydown'].forEach(function (ev) { window.removeEventListener(ev, stop, true); });
      }
      ['touchstart', 'wheel', 'keydown'].forEach(function (ev) { window.addEventListener(ev, stop, { capture: true, passive: true }); });
      setTimeout(stop, 2600);
    }

    kindCard(kind, title, price, sub) {
      var on = this.state.kind === kind;
      return '<button type="button" class="pc__kind' + (on ? ' is-on' : '') + '" data-act="kind" data-kind="' + kind + '" aria-pressed="' + on + '">' +
        '<span class="pc__kind-top"><span class="pc__kind-title">' + esc(title) + '</span><span class="pc__kind-price">' + esc(price) + '</span></span>' +
        (sub ? '<span class="pc__kind-sub">' + esc(sub) + '</span>' : '') + '</button>';
    }

    // THE PLAN SWITCH, in the cart (William, 2026-10-06: "enable a change of the once per month
    // and twice from the shopping cart view … without moving to another page"). The cadence is a
    // variant of the box product, so the switch is the other variant on the same line: remove the
    // line, add the other variant with the same quantity and selling plan, and let the theme redraw.
    boxLineFacts() {
      try { var f = JSON.parse(this.dataset.boxLine || ''); return f && f.key && f.variants ? f : null; } catch (e) { return null; }
    }
    planSwitch() {
      var f = this.boxLineFacts();
      if (!f || f.variants.length < 2) return '';
      var html = '<div class="pc__plan">';
      f.variants.forEach(function (v) {
        var on = v.id === f.variantId;
        var label = /twice/i.test(v.title) ? 'Twice a month' : /once/i.test(v.title) ? 'Once a month' : v.title;
        html += '<button type="button" data-act="plan" data-variant="' + v.id + '" class="' + (on ? 'is-on' : '') + '" aria-pressed="' + on + '">' + esc(label) + '</button>';
      });
      return html + '</div><div class="pc__plan-hint">Change your plan here; the box and the price update in your cart.</div>';
    }
    // WHERE THE BOX IS PICKED UP (William, 2026-10-07: "ask it in the cart too"): in store or a route
    // stop, saved on the cart (`_Box-Pickup-Stop` = the stop id, `Box pickup` = what the customer
    // reads). The orders webhook writes it as the member's box spot (subscription_delivery_prefs),
    // the same record the account's Box membership page edits.
    boxSpotCard(inStore, routes) {
      var cur = this.boxStop;
      var html = '<div class="pc__card"><div class="pc__card-title"><span>Where do you pick up your box?</span></div>';
      html += '<select id="pc-box-stop" class="pc__select"><option value="">Choose in store or a route stop\u2026</option>';
      if (inStore && inStore.stops && inStore.stops[0]) html += '<option value="' + esc(inStore.stops[0].id) + '"' + (inStore.stops[0].id === cur ? ' selected' : '') + '>In store at the market</option>';
      routes.forEach(function (r) {
        html += '<optgroup label="' + esc(r.label) + '">';
        r.stops.forEach(function (st) { html += '<option value="' + esc(st.id) + '"' + (st.id === cur ? ' selected' : '') + '>' + esc(st.label + (st.time ? ' \u00b7 ' + st.time : '')) + '</option>'; });
        html += '</optgroup>';
      });
      html += '</select><div class="pc__hint">' + (cur ? '\u2713 Saved for your box. You can change it later in your account.' : 'Needed to check out.') + '</div></div>';
      return html;
    }
    saveBoxStop(id, label) {
      var self = this;
      this.boxStop = id;
      document.querySelectorAll('[data-swr-box-spot]').forEach(function (e) { e.textContent = id ? label : 'Not chosen yet'; });
      fetch(rootUrl() + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: { '_Box-Pickup-Stop': id, 'Box pickup': id ? label : '' } }) })
        .then(function () { var h = document.querySelector('[data-swr-membership-slot] #pc-box-stop'); if (h && h.nextElementSibling) h.nextElementSibling.textContent = id ? '\u2713 Saved for your box. You can change it later in your account.' : 'Needed to check out.'; self.gate(self.mobileOk); })
        .catch(function () {});
    }
    switchPlan(variantId, button) {
      var f = this.boxLineFacts();
      if (!f || variantId === f.variantId) return;
      document.querySelectorAll('.pc__plan button').forEach(function (b) { b.disabled = true; });
      var item = { id: variantId, quantity: f.quantity };
      if (f.sellingPlan) item.selling_plan = f.sellingPlan;
      fetch(rootUrl() + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: f.key, quantity: 0 }) })
        .then(function () { return fetch(rootUrl() + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [item] }) }); })
        .then(function (r) { if (!r.ok) throw new Error('add ' + r.status); location.reload(); })
        .catch(function () { location.reload(); });
    }

    bind() {
      var self = this;
      this.querySelectorAll('[data-act]').forEach(function (el) {
        var act = el.dataset.act;
        el.addEventListener(el.tagName === 'SELECT' ? 'change' : 'click', function (e) {
          e.preventDefault();
          if (act === 'plan') { self.switchPlan(Number(el.dataset.variant), el); return; }
          if (act === 'edit') { self.editing = true; self.render(); self.reveal(); return; }
          if (act === 'keep') { self.editing = false; self.adoptSaved(); self.render(); self.reveal(); return; }
          if (act === 'save') { self.save(); return; }
          if (act === 'kind') {
            var kind = el.dataset.kind;
            if (kind === self.state.kind) return;
            // Switching route ↔ in store is a change like any other (William, 2026-10-07): the form
            // turns unsaved and shows Save. In store keeps the day already picked when it is offered.
            var keep = '';
            if (kind === 'in-store') {
              var store = self.options.routes.filter(function (r) { return r.kind === 'in-store'; })[0];
              var dates = store && store.stops[0] ? store.stops[0].dates : [];
              if (self.state.date && dates.some(function (d) { return d.date === self.state.date; })) keep = self.state.date;
            }
            self.state = { kind: kind, stopId: '', date: keep, time: '' };
          }
          if (act === 'stop') self.state = { kind: 'route', stopId: el.value, date: '', time: '' };
          if (act === 'date') { self.state.date = el.dataset.value; self.state.time = ''; }
          if (act === 'time') self.state.time = el.dataset.value;
          writeDraft(self.state);
          self.editing = true;
          if (self.complete()) self.save(); else { self.render(); self.reveal(); }
        });
      });
    }

    bindMobile() {
      var self = this;
      var input = this.querySelector('#pc-mobile');
      var hint = this.querySelector('.pc__mobile-hint');
      var savedAs = usPhone(this.saved.mobile);
      function show(ok, text) {
        hint.textContent = text;
        hint.classList.toggle('is-bad', !ok);
        // The checkout opens on a saved pickup AND a number, never on the number alone (gate() adds the box spot).
        self.mobileOk = !!ok;
        self.gate(ok);
      }
      var OK = '\u2713 Saved';
      var NEED = 'Needed to check out.';
      show(!!savedAs, savedAs ? OK : NEED);
      var sms = this.querySelector('#pc-sms');
      sms.addEventListener('change', function () {
        var v = sms.checked ? SMS_CONSENT_VERSION : '';
        fetch(rootUrl() + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { 'SMS-Consent': v } }),
        }).then(function () { self.saved.sms = sms.checked; return syncFeeLine({ '_SMS-Consent': v }); });
      });
      var timer = null;
      input.addEventListener('input', function () {
        clearTimeout(timer);
        var phone = usPhone(input.value);
        if (!phone) { show(false, input.value.replace(/\D/g, '').length >= 10 ? 'Please enter a 10-digit US mobile number.' : NEED); return; }
        timer = setTimeout(function () {
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          fetch(root + 'cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { Mobile: phone } }),
          }).then(function (r) {
            if (!r.ok) throw new Error('mobile ' + r.status);
            self.saved.mobile = phone;
            show(true, OK);
            return syncFeeLine({ _Mobile: phone });
          }).catch(function () { show(false, 'We could not save your number \u2014 please try again.'); });
        }, 400);
      });
      // A signed-in customer is not asked again (William, 2026-10-06): the number we already know
      // fills the box and is saved on the cart like a typed one. They can still change it here.
      if (!savedAs && this.knownPhone) {
        input.value = this.knownPhone;
        input.dispatchEvent(new Event('input'));
      }
    }

    complete() {
      var s = this.state;
      if (s.kind === 'in-store') return !!(s.date && s.time);
      return !!(s.kind === 'route' && s.stopId && s.date);
    }

    save() {
      var s = this.state;
      var inStore = this.options.routes.filter(function (r) { return r.kind === 'in-store'; })[0];
      var hit = s.kind === 'in-store' ? { route: inStore, stop: inStore.stops[0] } : this.findStopById(s.stopId);
      var day = hit && hit.stop.dates.filter(function (d) { return d.date === s.date; })[0];
      var fee = hit && this.feeFor(hit.route);
      if (!hit || !day || !fee) { this.render(); return; }

      var isStore = hit.route.kind === 'in-store';
      var a = hit.stop.address;
      var attributes = {
        'Pickup-Location-Id': hit.stop.locationId,
        'Pickup-Date': slashDate(day.date),
        'Pickup-Time': isStore ? s.time : '',
        'Pickup-Location-Company': hit.stop.company,
        'Pickup-Location-Address-Line-1': a.line1,
        'Pickup-Location-City': a.city,
        'Pickup-Location-Region': a.region,
        'Pickup-Location-Postal-Code': a.postalCode,
        'Pickup-Location-Country': a.country,
        'Checkout-Method': 'pickup',
        'Custom-Attribute-1': hit.route.label,
        'Pickup-Source': 'swrfm-chooser',
      };
      var where = [a.line1, a.city].filter(Boolean).join(', ');
      var properties = { Stop: isStore ? 'In-store at the market' + (where ? ', ' + where : '') : hit.stop.label + ' (' + hit.route.label + ')', Day: day.label };
      properties.Time = isStore ? s.time : (hit.stop.time || '');
      // The same facts again as hidden (_-prefixed) properties ON THE LINE. Shopify documents that
      // accelerated checkout buttons do not support cart attributes; a line's properties are part of
      // the line itself, so whatever path the customer pays by, the order still carries its stop,
      // date and mobile. normalize.ts reads these when the attributes are missing.
      properties['_Pickup-Location-Id'] = hit.stop.locationId;
      properties['_Pickup-Date'] = slashDate(day.date);
      properties['_Pickup-Time'] = isStore ? s.time : '';
      properties['_Pickup-Location-Company'] = hit.stop.company;
      properties['_Pickup-Source'] = 'swrfm-chooser';
      if (this.saved.mobile) properties._Mobile = this.saved.mobile;
      if (this.saved.sms) properties['_SMS-Consent'] = 'yes';

      var busy = this.querySelector('.pc');
      if (busy) busy.classList.add('pc--busy');
      var msg = this.querySelector('.pc__gate-msg'), btn = this.querySelector('[data-act="save"]');
      if (msg) msg.textContent = 'Saving your pickup…';
      if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var self = this;
      feeLineZeroes().then(function (updates) {
        return fetch(root + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes, updates: updates }),
        });
      }).then(function (r) {
        if (!r.ok) throw new Error('update ' + r.status);
        return fetch(root + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: fee.id, quantity: 1, properties: properties }] }),
        });
      }).then(function (r) {
        if (!r.ok) throw new Error('add ' + r.status);
        try { sessionStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
        return self.softRefresh();
      }).catch(function () {
        if (busy) busy.classList.remove('pc--busy');
        if (msg) msg.textContent = 'We could not save your pickup. Please try again.';
        if (btn) { btn.disabled = false; btn.textContent = 'Save pickup'; }
      });
    }

    clear() {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var attributes = {};
      ['Pickup-Location-Id', 'Pickup-Date', 'Pickup-Time', 'Pickup-Location-Company', 'Pickup-Location-Address-Line-1',
        'Pickup-Location-City', 'Pickup-Location-Region', 'Pickup-Location-Postal-Code', 'Pickup-Location-Country',
        'Checkout-Method', 'Custom-Attribute-1', 'Pickup-Source'].forEach(function (k) { attributes[k] = ''; });
      feeLineZeroes().then(function (updates) {
        return fetch(root + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes, updates: updates }),
        });
      }).then(function () { if (typeof window.refreshCart === 'function') window.refreshCart(false); });
    }
  }

  customElements.define('pickup-chooser', PickupChooser);
})();
